import { useState } from "react";
import { ThumbsUp, ThumbsDown, Check, Flag } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { submitIssueFeedback } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const REASONS = [
    { id: "too_low", label: "Estimate too low" },
    { id: "too_high", label: "Estimate too high" },
    { id: "priority_wrong", label: "Priority feels off" },
    { id: "confusing", label: "Confusing wording" },
    { id: "missing", label: "Missing context" },
    { id: "other", label: "Other" },
];

const SESSION_KEY = "bl_issue_feedback_v1";

/**
 * IssueFeedbackInline — tiny "flag this finding" button that opens a calm
 * popover. Pure DB → never invokes AI. Persists locally so the same user
 * doesn't see the open-state come back after they've submitted.
 */
export default function IssueFeedbackInline({ reportId, issue }) {
    const issueId = issue?.issue_id;
    const submittedKey = `${SESSION_KEY}:${reportId}:${issueId}`;
    const initialDone = (() => {
        try {
            return sessionStorage.getItem(submittedKey) === "1";
        } catch {
            return false;
        }
    })();
    const [done, setDone] = useState(initialDone);
    const [reason, setReason] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useState(false);

    if (!reportId || !issueId) return null;

    const send = async (feedbackType, payloadOverrides = {}) => {
        setSubmitting(true);
        const exposure = issue?.cost_exposure || {};
        const payload = {
            report_id: reportId,
            issue_id: issueId,
            feedback_type: feedbackType,
            issue_category: issue?.category || null,
            issue_type: issue?.title || null,
            displayed_repair_low: exposure?.estimated_repair_range_usd?.low ?? null,
            displayed_repair_high: exposure?.estimated_repair_range_usd?.high ?? null,
            confidence_level: exposure?.confidence || null,
            comment: payloadOverrides.comment || null,
        };
        try {
            await submitIssueFeedback(payload);
            track("issue_feedback_submitted", {
                report_id: reportId,
                issue_id: issueId,
                feedback_type: feedbackType,
            });
            try {
                sessionStorage.setItem(submittedKey, "1");
            } catch {
                /* ignore */
            }
            setDone(true);
            setOpen(false);
            toast.success("Thanks — feedback noted.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save feedback.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-bl-teal-tint text-bl-primary text-[11px]"
                data-testid={`issue-feedback-done-${issueId}`}
            >
                <Check className="w-3 h-3" />
                Feedback sent
            </span>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-bl-surface border border-bl text-bl-muted hover:text-bl-primary hover:border-bl-primary transition text-xs"
                    data-testid={`issue-feedback-open-${issueId}`}
                    title="Tell us if this finding feels off"
                >
                    <Flag className="w-3 h-3" />
                    Flag estimate
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-80 p-4 bg-bl-surface"
                data-testid={`issue-feedback-popover-${issueId}`}
                align="start"
            >
                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                    Quick feedback
                </div>
                <h4 className="mt-1 font-display text-bl text-base leading-tight">
                    How does this finding feel?
                </h4>
                <div className="mt-3 flex gap-2">
                    <button
                        type="button"
                        onClick={() => send("helpful")}
                        disabled={submitting}
                        className="bl-btn-secondary text-xs flex-1 justify-center"
                        data-testid={`issue-feedback-helpful-${issueId}`}
                    >
                        <ThumbsUp className="w-3.5 h-3.5" /> Helpful
                    </button>
                    <button
                        type="button"
                        onClick={() => setReason((r) => r || "too_low")}
                        disabled={submitting}
                        className="bl-btn-secondary text-xs flex-1 justify-center"
                        data-testid={`issue-feedback-needs-work-${issueId}`}
                    >
                        <ThumbsDown className="w-3.5 h-3.5" /> Needs work
                    </button>
                </div>
                {reason && (
                    <div className="mt-3">
                        <div className="flex flex-wrap gap-1.5">
                            {REASONS.map((r) => (
                                <button
                                    key={r.id}
                                    type="button"
                                    onClick={() => setReason(r.id)}
                                    className={
                                        "px-2 py-1 rounded-full text-[11px] " +
                                        (reason === r.id
                                            ? "bg-bl-primary text-bl-surface"
                                            : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                                    }
                                    data-testid={`issue-feedback-reason-${issueId}-${r.id}`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            placeholder="What felt off? (optional)"
                            className="mt-2 w-full px-2.5 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-xs resize-y"
                            maxLength={2000}
                            data-testid={`issue-feedback-comment-${issueId}`}
                        />
                        <div className="mt-2 flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setReason(null);
                                    setComment("");
                                }}
                                className="text-xs text-bl-muted hover:text-bl px-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    send(reason, { comment: comment.trim() || null })
                                }
                                disabled={submitting || !reason}
                                className="bl-btn-primary text-xs"
                                data-testid={`issue-feedback-submit-${issueId}`}
                            >
                                {submitting ? "Sending…" : "Send"}
                            </button>
                        </div>
                    </div>
                )}
                <p className="mt-3 text-[10px] text-bl-muted leading-relaxed">
                    Your feedback shapes future estimates. It's never shared
                    publicly.
                </p>
            </PopoverContent>
        </Popover>
    );
}
